export type FormStepId =
	| 'get-started'
	| 'personal-info'
	| 'company'
	| 'primary-contacts'
	| 'products'
	| 'facilities'
	| 'adverse-events'
	| 'suppliers-testing'
	| 'label-governance'
	| 'records-access'
	| 'recall-preparedness'
	| 'training-documentation'
	| 'regulatory-monitoring'
	| 'policies-references'
	| 'generate'

export type FormStep = {
	readonly id: FormStepId
	readonly path: string
	readonly title: string
	readonly order: number
}

export const FORM_STEPS: readonly FormStep[] = [
	{
		id: 'get-started',
		path: '/mocra/get-started',
		title: 'Get Started',
		order: 1,
	},
	{
		id: 'personal-info',
		path: '/mocra/personal-info',
		title: 'Your Information',
		order: 2,
	},
	{ id: 'company', path: '/mocra/company', title: 'Company', order: 3 },
	{
		id: 'primary-contacts',
		path: '/mocra/primary-contacts',
		title: 'Primary Contacts',
		order: 4,
	},
	{ id: 'products', path: '/mocra/products', title: 'Products', order: 5 },
	{
		id: 'facilities',
		path: '/mocra/facilities',
		title: 'Facilities',
		order: 6,
	},
	{
		id: 'adverse-events',
		path: '/mocra/adverse-events',
		title: 'Adverse Event Ops',
		order: 7,
	},
	{
		id: 'suppliers-testing',
		path: '/mocra/suppliers-testing',
		title: 'Suppliers & Testing',
		order: 8,
	},
	{
		id: 'label-governance',
		path: '/mocra/label-governance',
		title: 'Label Governance',
		order: 9,
	},
	{
		id: 'records-access',
		path: '/mocra/records-access',
		title: 'Records Access',
		order: 10,
	},
	{
		id: 'recall-preparedness',
		path: '/mocra/recall-preparedness',
		title: 'Recall Preparedness',
		order: 11,
	},
	{
		id: 'training-documentation',
		path: '/mocra/training-documentation',
		title: 'Training & Docs',
		order: 12,
	},
	{
		id: 'regulatory-monitoring',
		path: '/mocra/regulatory-monitoring',
		title: 'Regulatory Monitoring',
		order: 13,
	},
	{
		id: 'policies-references',
		path: '/mocra/policies-references',
		title: 'Policies & References',
		order: 14,
	},
	{
		id: 'generate',
		path: '/mocra/generate',
		title: 'Generate Package',
		order: 15,
	},
] as const

const STEP_BY_ID = new Map<FormStepId, FormStep>(
	FORM_STEPS.map((step) => [step.id, step]),
)

const STEP_BY_PATH = new Map<string, FormStep>(
	FORM_STEPS.map((step) => [normalizePath(step.path), step]),
)

export function getFormStep(id: FormStepId): FormStep {
	const step = STEP_BY_ID.get(id)
	if (!step) {
		throw new Error(`Unknown MoCRA form step id: ${id}`)
	}
	return step
}

export function getFormStepByPath(path: string): FormStep | undefined {
	return STEP_BY_PATH.get(normalizePath(path))
}

export function getNextFormStepId(id: FormStepId): FormStepId | null {
	const current = getFormStep(id)
	const index = FORM_STEPS.indexOf(current)
	if (index < 0 || index + 1 >= FORM_STEPS.length) return null
	const nextStep = FORM_STEPS[index + 1]
	if (!nextStep) return null
	return nextStep.id
}

export function getPreviousFormStepId(id: FormStepId): FormStepId | null {
	const current = getFormStep(id)
	const index = FORM_STEPS.indexOf(current)
	if (index <= 0) return null
	const previousStep = FORM_STEPS[index - 1]
	if (!previousStep) return null
	return previousStep.id
}

export function buildStepPath(
	path: string,
	_searchParams: URLSearchParams,
): string {
	return normalizePath(path)
}

export function buildStepPathFromId(
	id: FormStepId,
	searchParams: URLSearchParams,
): string {
	return buildStepPath(getFormStep(id).path, searchParams)
}

export function formatStepOrder(step: FormStep): string {
	return step.order.toString().padStart(2, '0')
}

function normalizePath(path: string): string {
	return path.endsWith('/') && path !== '/' ? path.replace(/\/+$/, '') : path
}
